import React, { useCallback, useRef } from "react";
import { Panel, Tab, Tabs } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { setSettingsPage } from "../actions/session";
import EmptyPlaceholder from "../controls/EmptyPlaceholder";
import { useExtensionObjects } from "../ExtensionProvider";
import makeReactive from "../util/makeReactive";
import startupSettings from "../util/startupSettings";
import MainPage from "./MainPage";
const registerSettings = (_instanceGroup, title, component, props, visible, priority) => {
    return { title, component, props, visible, priority: priority || 100 };
};
export const GameSettings = () => {
    const { t } = useTranslation(["common"]);
    const dispatch = useDispatch();
    // Get extension objects using the hook instead of HOC
    const settingsPages = useExtensionObjects(registerSettings);
    const settingsPage = useSelector((state) => state.session.base.settingsPage || undefined);
    const startupSettingsRef = useRef(makeReactive(startupSettings));
    const changeStartup = useCallback((key, value) => {
        startupSettingsRef.current[key] = value;
    }, []);
    const setCurrentPage = useCallback((eventKey) => {
        if (typeof eventKey === "string") {
            dispatch(setSettingsPage(eventKey));
        }
    }, [dispatch]);
    const sortByPriority = (lhs, rhs) => {
        return lhs.priority - rhs.priority;
    };
    const renderTabElement = (page, idx) => {
        const props = page.props ? page.props() : {};
        return (React.createElement(Panel, { key: idx },
            React.createElement(Panel.Body, null,
                idx !== 0 ? React.createElement("hr", { style: { marginTop: 0 } }) : null,
                React.createElement(page.component, { ...props, changeStartup: changeStartup, startup: startupSettingsRef.current }))));
    };
    // Check if a setting is per-game (has a visibility condition and is currently visible)
    const isPerGameSetting = (ele) => ele.visible !== undefined && ele.visible();
    const renderTab = (page) => {
        // Only show per-game settings (has visibility condition AND currently visible)
        const elements = page.elements
            .filter(isPerGameSetting)
            .sort((lhs, rhs) => lhs.priority - rhs.priority);
        const content = elements.length > 0 ? (React.createElement("div", null, elements.map(renderTabElement))) : (React.createElement(EmptyPlaceholder, { fill: true, icon: "settings", subtext: t("This game has no specific settings in this category."), text: t("Nothing to configure.") }));
        return (React.createElement(Tab, { eventKey: page.title, key: page.title, title: t(page.title) },
            React.createElement("div", null, content)));
    };
    // Filter out tabs that have no per-game settings for the current game
    const tabsWithPerGameSettings = (tabs) => tabs.filter((tab) => tab.elements.some(isPerGameSetting));
    // Combine all settings by title
    const combined = settingsPages.reduce((prev, current) => {
        const result = prev.slice();
        const existingPage = prev.find((ele) => ele.title === current.title);
        if (existingPage === undefined) {
            result.push({
                title: current.title,
                elements: [current],
                priority: current.priority,
            });
        }
        else {
            existingPage.elements.push(current);
            if (existingPage.priority === undefined ||
                current.priority < existingPage.priority) {
                existingPage.priority = current.priority;
            }
        }
        return result;
    }, []);
    // Only show tabs that have at least one per-game setting for the current game
    const visibleTabs = tabsWithPerGameSettings(combined).sort(sortByPriority);
    if (visibleTabs.length === 0) {
        return (React.createElement(MainPage, null,
            React.createElement(MainPage.Body, null,
                React.createElement(EmptyPlaceholder, { fill: true, icon: "settings", subtext: t("No game-specific settings available."), text: t("Nothing to configure.") }))));
    }
    const page = visibleTabs.find((iter) => iter.title === settingsPage) !== undefined
        ? settingsPage
        : visibleTabs[0].title;
    return (React.createElement(MainPage, null,
        React.createElement(MainPage.Body, null,
            React.createElement(Tabs, { activeKey: page, id: "game-settings-tab", onSelect: setCurrentPage }, visibleTabs.map(renderTab)))));
};
export default GameSettings;
